import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import '../styles/Discussions.css';
import { formatTimeCompact } from '../utils/formatTimeCompact';
import { getHeartIcon, getCommentsIcon } from '../utils/getThemeIcon';
import { getAuthorIconUrl } from '../utils/getAuthorPlaceholder';
import { ThemeContext } from '../context/ThemeContext';
import { ApiContext } from '../context/ApiContext';
import { useDemo } from '../context/DemoContext';

const Discussions = () => {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likedDiscussionIds, setLikedDiscussionIds] = useState([]);
  const [expandedDiscussions, setExpandedDiscussions] = useState({});
  const { getDiscussions, callApi } = useAuth();
  const navigate = useNavigate();
  const apiUrl = useContext(ApiContext);
  const { theme } = useContext(ThemeContext);
  const { isDemoMode } = useDemo();

  // Helper function to count lines in discussion content
  const countDiscussionLines = (text) => {
    if (!text) return 0;
    return text.split('\n').length;
  };

  // Helper function to toggle discussion expansion
  const toggleDiscussionExpansion = (discussionId) => {
    setExpandedDiscussions(prev => ({
      ...prev,
      [discussionId]: !prev[discussionId]
    }));
  };

  const handleDiscussionClick = (discussion) => {
    navigate(`/discussion/${discussion.id}`, { state: { initialDiscussion: discussion } });
  };

  const handleLikeDiscussion = async (discussionId) => {
    try {
      const likeUrl = apiUrl ? `${apiUrl}/api/v1/discussion/like` : '/api/v1/discussion/like';
      const response = await callApi(likeUrl, 'POST', { id: discussionId });
      if (response.ok) {
        // Refresh liked status
        const likedResponse = await callApi(
          apiUrl ? `${apiUrl}/api/v1/discussion/liked` : '/api/v1/discussion/liked',
          'POST',
          { ids: [discussionId] }
        );
        if (likedResponse.ok) {
          const likedData = await likedResponse.json();
          setLikedDiscussionIds(likedData.ids);
        }
      } else {
        console.error('Failed to like discussion');
      }
    } catch (error) {
      console.error('Error while liking discussion:', error);
    }
  };

  useEffect(() => {
    const fetchDiscussions = async () => {
      if (isDemoMode) {
        setDiscussions([]);
        setLoading(false);
        return;
      }
      try {
        const response = await getDiscussions();
        if (response.ok) {
          const data = await response.json();
          setDiscussions(data);
        } else {
          throw new Error('Failed to fetch discussions');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussions();
  }, []);

  // useEffect(() => {
  //   const fetchLikedDiscussions = async () => {
  //     if (discussions.length === 0) {
  //       setLikedDiscussionIds([]);
  //       return;
  //     }

  //     try {
  //       const likedUrl = apiUrl ? `${apiUrl}/api/v1/discussion/liked` : '/api/v1/discussion/liked';
  //       const likedResponse = await callApi(
  //         likedUrl,
  //         'POST',
  //         { ids: discussions.map((discussion) => discussion.id) }
  //       );
  //       if (likedResponse.ok) {
  //         const likedData = await likedResponse.json();
  //         setLikedDiscussionIds(likedData.ids);
  //       } else {
  //         console.error('Failed to fetch liked discussions');
  //       }
  //     } catch (error) {
  //       console.error('Error fetching liked discussions:', error);
  //       setLikedDiscussionIds([]);
  //     }
  //   };

  //   fetchLikedDiscussions();
  // }, [apiUrl, discussions, callApi]);

  if (loading) return <div className="discussions-loading">Loading discussions...</div>;
  if (error) return <div className="discussions-error">{error}</div>;

  return (
    <div className="discussions">
      <div className="discussions-scrollable">
        <ul>
          {discussions.map((discussion) => (
            <li 
              key={discussion.id} 
              className="discussion-item"
              onClick={() => handleDiscussionClick(discussion)}
            >
              <img 
                src={getAuthorIconUrl(discussion, apiUrl, theme)}
                alt="Author"
                className="discussion-author-icon"
              />
              <div className="discussion-body">
                <div className="discussion-header">
                  <p className="discussion-author">@{discussion.authorUsername}</p>
                  <p className="discussion-date">{formatTimeCompact(discussion.createdAt)}</p>
                </div>
                {countDiscussionLines(discussion.content) > 3 && !expandedDiscussions[discussion.id] ? (
                  <>
                    <p className="discussion-content discussion-content-collapsed">
                      {discussion.content.split('\n').slice(0, 3).join('\n')}
                    </p>
                    <div className="discussion-expand-button-container">
                      <button
                        className="discussion-expand-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDiscussionExpansion(discussion.id);
                        }}
                      >
                        ...show more
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="discussion-content">
                    {discussion.content}
                  </p>
                )}
                {expandedDiscussions[discussion.id] && countDiscussionLines(discussion.content) > 3 && (
                  <div className="discussion-expand-button-container">
                    <button
                      className="discussion-expand-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDiscussionExpansion(discussion.id);
                      }}
                    >
                      show less
                    </button>
                  </div>
                )}
                <div className="discussion-actions-container">
                  <div className="discussion-like-action">
                    <img
                      src={getHeartIcon(theme, likedDiscussionIds.includes(discussion.id))}
                      alt="Like"
                      className="discussion-like-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikeDiscussion(discussion.id);
                      }}
                    />
                    <span>{discussion.likesCount || 0}</span>
                  </div>
                  {discussion.commentsCount > 0 && (
                    <div className="discussion-comment-action">
                      <img
                        className="discussion-comment-icon"
                        src={getCommentsIcon(theme)}
                        alt="Comments"
                      />
                      <span>{discussion.commentsCount}</span>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Discussions;
